import { IBaseStationResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export const baseStationDefaultSort: DataSourceSort[] = [{ field: 'Name', dir: 'asc' }];

export type BaseStationsFilter = Readonly<{
    corporationId?: string;
    locationId?: string;
    includeState?: boolean;
    nameSearch: string | undefined;
    hasActiveIssues?: boolean;
}>;

export const buildFilterExpression = ({
    corporationId,
    locationId,
    hasActiveIssues,
}: Partial<BaseStationsFilter>) =>
    Filter.and(
        locationId ? Filter.eq('LocationId', locationId) : undefined,
        corporationId ? Filter.eq('CorporationId', corporationId) : undefined,
        hasActiveIssues ? Filter.gt('ActiveIssueCount', 0) : undefined,
    );

export type UseBaseStationDataSourceOptions = BaseDataSourceOptions<
    IBaseStationResponse,
    BaseStationsFilter
>;

export type BaseStationDataSource = PagedDataSource<
    IBaseStationResponse,
    BaseStationsFilter
>;

export type BaseStationInfiniteDataSource = InfiniteDataSource<
    IBaseStationResponse,
    BaseStationsFilter
>;

const getAllBaseStations: DataSourceQuery<IBaseStationResponse, BaseStationsFilter> = ({
    filter: { includeState, nameSearch, ...filterRest },
    sort,
    page,
    pageLimit,
}) => {
    const filter = buildFilterExpression(filterRest);
    const orderBy = buildOrderBy(sort);
    return toDataSourceResult(
        sdClient.getBaseStations({
            includeState: includeState ?? true,
            filter,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        }),
    );
};

export function usePagedBaseStationDataSource({
    defaultSort = baseStationDefaultSort,
    ...options
}: UseBaseStationDataSourceOptions): BaseStationDataSource {
    return usePagedDataSource({
        defaultSort,
        ...options,
        query: getAllBaseStations,
    });
}

export function useInfiniteBaseStationDataSource(
    options: UseBaseStationDataSourceOptions,
): BaseStationInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllBaseStations,
    });
}
